package diana.soleil.design.iraniancanadienbazzar.notifications

import diana.soleil.design.iraniancanadienbazzar.core.common.DataResult
import diana.soleil.design.iraniancanadienbazzar.core.data.NotificationRepository
import diana.soleil.design.iraniancanadienbazzar.core.firebase.AuthService
import diana.soleil.design.iraniancanadienbazzar.core.firebase.PushMessagingService
import diana.soleil.design.iraniancanadienbazzar.locale.LocaleController
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.collectLatest
import kotlinx.coroutines.flow.combine
import org.koin.dsl.module

/**
 * Keeps the signed-in account's FCM registration fresh. Native entry points request notification
 * permission; this shared worker retries briefly because iOS may receive its APNs token after auth
 * restoration and composition startup.
 *
 * The registration also carries the language THIS installation shows, and re-registers the moment it
 * changes, so server pushes are localized per device instead of per account (docs/DATA_MODEL.md).
 */
class PushTokenSync(
    private val auth: AuthService,
    private val messaging: PushMessagingService,
    private val notifications: NotificationRepository,
    private val locale: LocaleController,
) {
    suspend fun run() {
        combine(auth.authState, locale.language) { user, language -> user to language.code }
            .collectLatest { (user, language) ->
                if (user == null || user.isAnonymous) return@collectLatest
                while (true) {
                    val registered = registerCurrentToken(language)
                    delay(if (registered) TOKEN_REFRESH_MILLIS else FAILED_RETRY_MILLIS)
                }
            }
    }

    private suspend fun registerCurrentToken(language: String): Boolean {
        repeat(MAX_ATTEMPTS) { attempt ->
            val token = runCatching { messaging.getToken() }.getOrNull()
            if (!token.isNullOrBlank() &&
                notifications.registerDevice(token, language) is DataResult.Success
            ) {
                return true
            }
            if (attempt < MAX_ATTEMPTS - 1) {
                delay(RETRY_BASE_MILLIS * (attempt + 1))
            }
        }
        return false
    }

    private companion object {
        const val MAX_ATTEMPTS = 5
        const val RETRY_BASE_MILLIS = 1_000L
        const val FAILED_RETRY_MILLIS = 60 * 60 * 1_000L
        const val TOKEN_REFRESH_MILLIS = 24 * 60 * 60 * 1_000L
    }
}

val notificationsModule = module {
    single { PushTokenSync(get(), get(), get(), get()) }
}
